import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { REDIRECT_COUNTDOWN } from '../utils/constants';

/**
 * Custom hook for countdown redirect functionality
 * Used in success/cancel pages for auto-redirect
 * 
 * @param {string} redirectPath - Path to redirect to
 * @param {number} initialCount - Initial countdown value (default: 10)
 * @param {boolean} startImmediately - Whether to start countdown immediately
 * @returns {Object} Countdown state and controls
 */
export const useCountdownRedirect = (
    redirectPath,
    initialCount = REDIRECT_COUNTDOWN,
    startImmediately = true
) => {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(initialCount);
    const [isActive, setIsActive] = useState(startImmediately);
    const intervalRef = useRef(null);

    /**
     * Start the countdown
     */
    const start = useCallback(() => {
        setIsActive(true);
    }, []);

    /**
     * Pause the countdown
     */
    const pause = useCallback(() => {
        setIsActive(false);
    }, []);

    /**
     * Reset countdown to initial value
     */
    const reset = useCallback(() => {
        setCountdown(initialCount);
    }, [initialCount]);

    /**
     * Cancel countdown and clear interval
     */
    const cancel = useCallback(() => {
        setIsActive(false);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    /**
     * Immediately navigate to redirect path
     */
    const navigateNow = useCallback(() => {
        cancel();
        navigate(redirectPath);
    }, [cancel, navigate, redirectPath]);

    // Handle countdown logic
    useEffect(() => {
        if (!isActive) return;

        intervalRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    navigate(redirectPath);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isActive, navigate, redirectPath]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return {
        countdown,
        isActive,
        start,
        pause,
        reset,
        cancel,
        navigateNow
    };
};

export default useCountdownRedirect;
